import React, { useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import './CombatOrderControls.scss';
export function useCombatOrderDrag({
  ids,
  enabled,
  onReorder
}) {
  const rows = useRef(new Map());
  const gesture = useRef(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const idsKey = ids.join('\n');
  useEffect(() => {
    gesture.current = null;
    setPreview(null);
  }, [idsKey, enabled]);
  const move = async (id, index) => {
    if (!enabled || saving) return;
    const next = ids.filter(value => value !== id);
    next.splice(index, 0, id);
    if (next.every((value, i) => value === ids[i])) return;
    setSaving(true);
    try {
      const saved = await onReorder(next);
      setAnnouncement(saved === false ? 'Order was not changed.' : `Combatant moved to position ${index + 1} of ${ids.length}.`);
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    gesture.current = null;
    setPreview(null);
  };
  const handleProps = id => ({
    onPointerDown: event => {
      if (!enabled || saving || event.button !== 0 || !event.isPrimary && event.isPrimary !== undefined) return;
      event.stopPropagation();
      event.preventDefault();
      gesture.current = {
        id,
        startY: event.clientY,
        index: ids.indexOf(id),
        moved: false,
        pointerId: event.pointerId
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerMove: event => {
      const current = gesture.current;
      if (!current || current.pointerId !== event.pointerId) return;
      event.stopPropagation();
      if (!current.moved && Math.abs(event.clientY - current.startY) < 5) return;
      current.moved = true;
      const others = ids.filter(value => value !== id);
      const index = others.filter(value => {
        const rect = rows.current.get(value)?.getBoundingClientRect();
        return rect && event.clientY > rect.top + rect.height / 2;
      }).length;
      current.index = index;
      setPreview({
        id,
        index,
        anchor: others[index] || others[others.length - 1],
        after: index === others.length
      });
    },
    onPointerUp: event => {
      const current = gesture.current;
      if (!current || current.pointerId !== event.pointerId) return;
      event.stopPropagation();
      cancel();
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      if (current.moved) void move(id, current.index);
    },
    onPointerCancel: cancel,
    onLostPointerCapture: cancel,
    onKeyDown: event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        cancel();
      }
    },
    onClick: event => event.stopPropagation()
  });
  const rowProps = id => ({
    ref: element => element ? rows.current.set(id, element) : rows.current.delete(id),
    'data-order-dragging': preview?.id === id || undefined,
    'data-order-drop': preview?.anchor === id ? preview.after ? 'after' : 'before' : undefined
  });
  return {
    handleProps,
    rowProps,
    move,
    saving,
    announcement,
    preview,
    enabled
  };
}
export default function CombatOrderControls({
  id,
  label,
  index,
  total,
  drag
}) {
  if (!drag.enabled) return null;
  const handle=drag.handleProps(id);
  return <div className="combat-order-controls">
    <button type="button" className="btn combat-order-handle" aria-label={`Drag ${label} to reorder initiative`} aria-keyshortcuts="ArrowUp ArrowDown" title="Drag to reorder initiative. Keyboard: Arrow Up / Arrow Down." disabled={drag.saving || total < 2} {...handle} onKeyDown={event=>{
      if(event.key==='ArrowUp' || event.key==='ArrowDown'){
        event.preventDefault();event.stopPropagation();
        const next=index+(event.key==='ArrowUp'?-1:1);
        if(next>=0 && next<total)void drag.move(id,next);
      }else handle.onKeyDown(event);
    }}><GripVertical size={18} /></button>
  </div>;
}
